/**
 * Who this browser should be talking to, and who it should stop talking to.
 *
 * Separated from the peer connections themselves because it is the part that
 * can be tested. Everything in `voice.ts` needs a microphone, a browser and
 * somebody at the other end; this needs none of those, and it is where the
 * mistakes are: connecting to somebody twice, connecting to yourself, or
 * keeping a connection to somebody who left.
 *
 * Same split as `countdown.ts` and `render/narrow.ts` — the rule out where a
 * test can reach it, the effect around it.
 */

/** Anybody the roster names, except ourselves. */
export function peersIn(roster: { readonly [userId: string]: unknown }, myId: string): string[] {
  const out: string[] = [];
  for (const userId of Object.keys(roster)) {
    if (userId !== myId) out.push(userId);
  }
  /*
   * Sorted, so two browsers walking the same roster do the same things in the
   * same order. Object key order is not a promise anybody made.
   */
  out.sort();
  return out;
}

/**
 * What changed since last time: who to call, and who to hang up on.
 *
 * The roster is the trigger for everything. Somebody arriving mid-conversation
 * shows up in `add` for everybody already in it, and everybody already in it
 * shows up in their `add` — which is how both ends start at once without
 * either being told to.
 */
export function reconcile(
  have: readonly string[],
  roster: { readonly [userId: string]: unknown },
  myId: string,
): { add: string[]; drop: string[] } {
  const want = peersIn(roster, myId);

  const add: string[] = [];
  for (const peer of want) {
    if (have.indexOf(peer) < 0) add.push(peer);
  }

  const drop: string[] = [];
  for (const peer of have) {
    /*
     * Dropped for leaving the conversation OR for leaving the match — the
     * roster does not distinguish, and neither should this. A connection to
     * somebody who is not in the roster is a connection to nobody.
     */
    if (want.indexOf(peer) < 0) drop.push(peer);
  }

  return { add, drop };
}

/**
 * Whether the table should be warned that this is a lot of people.
 *
 * Counted in **participants, not seats**: a six-seat room with four watchers is
 * a ten-way mesh, and it is the mesh that gets choppy. Every participant sends
 * their audio to every other one, so the load on each browser grows with the
 * size of the conversation and has nothing to do with how many chairs there
 * are.
 */
export const MESH_COMFORTABLE = 6;

export function meshIsCrowded(participants: number): boolean {
  return participants > MESH_COMFORTABLE;
}

/**
 * How many people would be in the conversation if we joined it.
 *
 * Counts us whether we are in the roster yet or not, so the warning on a join
 * button describes the table as it would be after pressing it rather than as it
 * is before.
 */
export function sizeAfterJoining(
  roster: { readonly [userId: string]: unknown },
  myId: string,
): number {
  return peersIn(roster, myId).length + 1;
}
